import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Moon, X, ShieldCheck } from "lucide-react";

/**
 * AfterHoursHotline
 *
 * A small, floating "Emergency Signing Hotline" module that only surfaces
 * between 7 PM and 7 AM (visitor's local clock).
 *
 * - Bottom-right, above the fold once discovered.
 * - Dismissible per-session via sessionStorage.
 * - Rechecks the time every minute so it appears/disappears cleanly around 7.
 */
export default function AfterHoursHotline() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const tick = () => setNow(new Date());
    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("kn_afterhours_dismissed") === "1";
    const hour = now.getHours();
    const isAfterHours = hour >= 19 || hour < 7; // 7 PM – 7 AM local
    setVisible(isAfterHours && !dismissed);
  }, [now]);

  const dismiss = () => {
    sessionStorage.setItem("kn_afterhours_dismissed", "1");
    setVisible(false);
  };

  const timeStr = now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          data-testid="afterhours-hotline"
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          transition={{ duration: 0.55, ease: [0.2, 0.7, 0.15, 1] }}
          className="fixed z-[60] bottom-6 right-6 lg:bottom-8 lg:right-8 max-w-[360px]"
          role="complementary"
          aria-label="After-hours emergency signing hotline"
        >
          <div
            className="relative rounded-2xl overflow-hidden text-white shadow-[0_30px_80px_-24px_rgba(212,175,55,0.6),0_20px_50px_-18px_rgba(0,0,0,0.7)] border border-[#D4AF37]/40 backdrop-blur-xl"
            style={{
              background:
                "linear-gradient(155deg, rgba(11,20,55,0.95) 0%, rgba(30,42,94,0.95) 55%, rgba(11,20,55,0.98) 100%)",
            }}
          >
            {/* Gold top bar */}
            <div
              className="h-[3px] w-full"
              style={{
                background:
                  "linear-gradient(90deg,#7F5F17 0%,#D4AF37 20%,#F8E9A1 50%,#D4AF37 80%,#7F5F17 100%)",
              }}
            />

            {/* Dismiss */}
            <button
              onClick={dismiss}
              aria-label="Dismiss after-hours hotline"
              data-testid="afterhours-dismiss"
              className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-5 lg:p-6">
              {/* Header */}
              <div className="flex items-center gap-3 mb-3">
                <span className="relative flex items-center justify-center w-9 h-9 rounded-full"
                      style={{ background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)" }}>
                  <Moon className="w-4 h-4 text-[#0B1437]" strokeWidth={2.2} />
                  <span className="absolute inset-0 rounded-full kn-live-ping" style={{ background: "rgba(248,233,161,0.55)", zIndex: -1 }} />
                </span>
                <div className="leading-tight">
                  <p className="text-[10.5px] uppercase tracking-[0.28em] font-semibold kn-metallic-gold-text">
                    After-Hours Hotline
                  </p>
                  <p className="text-[12px] text-slate-300 font-body mt-0.5">
                    Local time <span className="text-white font-medium">{timeStr}</span>
                  </p>
                </div>
              </div>

              {/* Body */}
              <p className="font-display text-[19px] lg:text-[21px] leading-snug tracking-tight text-white">
                It's after 7 PM — <span className="kn-metallic-gold-text italic font-extralight">emergency signings</span> still available.
              </p>

              <button
                onClick={() => setExpanded((e) => !e)}
                data-testid="afterhours-toggle-details"
                className="mt-3 text-[11px] uppercase tracking-[0.24em] font-semibold text-white/60 hover:text-white transition-colors"
              >
                {expanded ? "Hide details" : "What counts as emergency?"}
              </button>

              <AnimatePresence initial={false}>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <ul className="mt-3 space-y-1.5 text-[13px] text-slate-300 font-body">
                      <li>· Hospital & bedside signings</li>
                      <li>· Time-sensitive loan / real-estate closings</li>
                      <li>· POA, medical directives, guardianship</li>
                    </ul>
                    <p className="mt-3 text-[11.5px] text-slate-400 italic">
                      After-hours appointments include a modest convenience surcharge.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-4 flex flex-col gap-2">
                <a
                  href="tel:+15132966926"
                  data-testid="afterhours-call"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold text-[#0B1437] transition-transform hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-14px_rgba(212,175,55,0.75)]"
                  style={{ background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)" }}
                >
                  <Phone className="w-4 h-4" />
                  Call (513) 296-6926
                </a>
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-center text-white/50 flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" />
                  Insured &amp; Bonded — 24/7
                </p>
              </div>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
